import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PromotionalBanner } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { getAppRoute } from 'src/utils/routing';
import { ROUTE_SEARCH_PARAM, ROUTES } from 'src/constants/route';
import { REF_TAG } from 'src/constants/refTag';
import { BUNDLE_NAME, HOMEPAGE_STRING_ID } from 'src/constants/localization';
import useUserMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

const BANNER_THUMBNAIL_SRC = 'https://d19a85d3zffugi.cloudfront.net/assets/4d8f2633-d51d-43d5-9d39-1ef7e6112250/original.svg';

export interface HomepageCredentialBannerProps {
  /** Credential learning pathway id the banner links to (pilot: amazon-pricing-credential). */
  credentialId: string;
}

/**
 * Hero banner ingress at the top of the homepage promoting the credential experience.
 * The "Get started" CTA navigates to the credential page. The homepage gates rendering on
 * the credentials pilot weblab.
 */
const HomepageCredentialBanner: React.FC<HomepageCredentialBannerProps> = ({ credentialId }) => {
  const navigate = useNavigate();
  const [homepageBundle] = useBundle(BUNDLE_NAME.HOMEPAGE);
  const { clickMetric, viewMetric } = useUserMetrics();

  // CredentialBannerImpression: banner shown, once per credentialId. No data dependency, so emit on mount.
  const lastEmittedImpression = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastEmittedImpression.current === credentialId) return;
    lastEmittedImpression.current = credentialId;
    viewMetric({
      feature: FeatureName.CredentialBannerImpression,
      metadata: { credentialId },
    });
  }, [viewMetric, credentialId]);

  const handleGetStarted = () => {
    clickMetric({
      feature: FeatureName.CredentialBannerIngress,
      metadata: { url: window.location.href, credentialId },
    });
    navigate(getAppRoute(`${ROUTES.NEW_LEARN.CREDENTIAL}/${credentialId}?${ROUTE_SEARCH_PARAM.REF}=${REF_TAG.CREDENTIAL_BANNER}`));
  };

  return (
    <div className="mx-[28px]">
      <PromotionalBanner
        heading={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_HEADING)}
        body={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_BODY)}
        thumbnailSrc={BANNER_THUMBNAIL_SRC}
        thumbnailAlt=""
        cta={{ label: t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_CTA), onClick: handleGetStarted }}
        badge={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_BADGE)}
        richLayout="spotlight"
        showCloseButton={false}
        ariaLabel={t(homepageBundle, HOMEPAGE_STRING_ID.CREDENTIAL_BANNER_HEADING)}
      />
    </div>
  );
};

export default HomepageCredentialBanner;
